import React, { useEffect, useState } from "react";
import { hasPermission } from "../utils/permissions";
import InlineLoading from "./InlineLoading";

const CLIENT_TYPES = ["detalle", "mayorista", "importador"];

const CashRegisterForm = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  defaultName = "",
  availableUsers,
  isSaving,
}) => {
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [allowedClientTypes, setAllowedClientTypes] = useState([]);
  const [discountApproverUserId, setDiscountApproverUserId] = useState("");
  const [maxOpenSales, setMaxOpenSales] = useState("5");
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    setName(initialData?.name || defaultName || "");
    setIsActive(initialData?.isActive ?? true);
    setAllowedClientTypes(
      Array.isArray(initialData?.allowedClientTypes) ? initialData.allowedClientTypes : []
    );
    setDiscountApproverUserId(
      initialData?.discountApproverUserId ? String(initialData.discountApproverUserId) : ""
    );
    setMaxOpenSales(
      initialData?.maxOpenSales != null ? String(initialData.maxOpenSales) : "5"
    );
    const users =
      (initialData?.CashRegisterUsers || [])
        .filter((entry) => entry?.isActive !== false)
        .map((entry) => entry.userId || entry.User?.id)
        .filter(Boolean) || [];
    setAssignedUsers(users.map((id) => String(id)));
  }, [initialData, isOpen, defaultName]);

  if (!isOpen) return null;

  const toggleClientType = (type) => {
    setAllowedClientTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const toggleUser = (userId) => {
    setAssignedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("El nombre es obligatorio.");
      return;
    }
    const parsedMaxOpenSales = Number.parseInt(maxOpenSales, 10);
    if (!Number.isInteger(parsedMaxOpenSales) || parsedMaxOpenSales < 1) {
      setError("El limite de ventas pendientes debe ser un entero >= 1.");
      return;
    }

    const payload = {
      name: trimmedName,
      isActive: Boolean(isActive),
      allowedClientTypes,
      discountApproverUserId: discountApproverUserId ? Number(discountApproverUserId) : null,
      maxOpenSales: parsedMaxOpenSales,
      users: assignedUsers.map((id) => ({ userId: Number(id), isActive: true })),
    };
    onSave(payload);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[1000] flex justify-center items-start overflow-y-auto px-4 py-6"
      onClick={(event) => event.stopPropagation()}
    >
      <div
        className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl border border-gray-100 overflow-hidden max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 bg-gray-50">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">Cajas</p>
            <h2 className="text-lg font-semibold text-gray-900">
              {initialData ? "Editar caja" : "Nueva caja"}
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Configura límites, clientes permitidos y usuarios con acceso.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 bg-white border border-gray-200 rounded-full w-9 h-9 flex items-center justify-center"
          >
            ✕
          </button>
        </div>

        <form className="p-6 space-y-6" onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">Nombre</label>
              <input
                type="text"
                className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">Limite de ventas abiertas</label>
              <input
                type="number"
                min="1"
                className="mt-2 w-full border border-gray-300 rounded-lg p-2"
                value={maxOpenSales}
                onChange={(e) => setMaxOpenSales(e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
                Caja activa
              </label>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase mb-2 block">
              Tipos de cliente permitidos
            </label>
            <div className="flex flex-wrap gap-3">
              {CLIENT_TYPES.map((type) => (
                <label key={type} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={allowedClientTypes.includes(type)}
                    onChange={() => toggleClientType(type)}
                  />
                  {type}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase mb-2 block">
              Encargado de autorizacion de descuentos
            </label>
            <select
              className="w-full border border-gray-300 rounded-lg p-2"
              value={discountApproverUserId}
              onChange={(e) => setDiscountApproverUserId(e.target.value)}
            >
              <option value="">Sin encargado</option>
              {availableUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name || user.username}
                  {hasPermission(user, "branch.access.any") ? " (Admin)" : ""}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 uppercase mb-2 block">
              Usuarios con acceso
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {availableUsers.map((user) => (
                <label key={user.id} className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={assignedUsers.includes(String(user.id))}
                    onChange={() => toggleUser(String(user.id))}
                  />
                  <span>{user.name || user.username}</span>
                </label>
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex items-center justify-between border-t border-gray-100 pt-4">
            <div className="text-xs text-gray-500">
              El límite de ventas aplica por sesión abierta.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:text-gray-900"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                disabled={isSaving}
              >
                {isSaving ? <InlineLoading label="Guardando" /> : "Guardar"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CashRegisterForm;
